import {
  checkTransactionsDuplicates,
  parseBankTransactionsFile,
} from "@/apis/custom/customRoutes";
import { type Transaction, TransactionSide } from "@/apis/gql/types";
import SuccessBanner from "@/components/shared/banners/success-banner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/ui/stepper";
import { TypographyLarge } from "@/components/ui/typography";
import { useTenant } from "@/providers/session";
import { useCreateTransactionsFileImport } from "@/queries/transactions/create-transactions";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import {
  Landmark,
  Loader2,
  Route,
  TableProperties,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import BankAccountStep from "./bank-account-step";
import {
  parseAmount,
  parseCurrency,
  parseDate,
  parsePurpose,
} from "./import-parsing";
import MappingStep from "./mapping-step";
import ReviewStep from "./review-step";
import UploadStep from "./upload-step";

/** steps moved inside component to use translations */

export default function TransactionsFileImport() {
  const { companySlug } = useTenant();
  const navigate = useNavigate();
  const [step, setStep] = useState<number>(0);
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<Array<Record<string, string>> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [selectedBankAccountId, setSelectedBankAccountId] =
    useState<string>("");
  const { createTransactionsFileImport } = useCreateTransactionsFileImport();
  const [isCreating, setIsCreating] = useState(false);

  const [completeTransactions, setCompleteTransactions] = useState<
    Partial<Transaction>[]
  >([]);
  const [incompleteTransactions, setIncompleteTransactions] = useState<
    Partial<Transaction>[]
  >([]);
  const [duplicateTransactions, setDuplicateTransactions] = useState<
    Partial<Transaction>[]
  >([]);
  const [checkDuplicatesLoading, setCheckDuplicatesLoading] = useState(false);

  useEffect(() => {
    if (!file) {
      setParsed(null);
      setMapping({});
    }
  }, [file]);

  const steps = [
    {
      title: t("transactions.file_import.steps.upload.title"),
      description: t("transactions.file_import.steps.upload.description"),
      icon: Upload,
    },
    {
      title: t("transactions.file_import.steps.mapping.title"),
      description: t("transactions.file_import.steps.mapping.description"),
      icon: Route,
    },
    {
      title: t("transactions.file_import.steps.account.title"),
      description: t("transactions.file_import.steps.account.description"),
      icon: Landmark,
    },
    {
      title: t("transactions.file_import.steps.review.title"),
      description: t("transactions.file_import.steps.review.description"),
      icon: TableProperties,
    },
  ];

  return (
    <div className="bg-background flex-1 h-screen overscroll-none">
      <div className="sticky top-0 z-10 bg-white">
        <div className="flex flex-row justify-between items-center px-4 py-2">
          <h2 className="text-xl font-semibold">
            {t("transactions.file_import.title")}
          </h2>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-muted-foreground gap-2"
          >
            <X />
            {t("payments.close")}
          </Button>
        </div>
        <Stepper steps={steps} currentStep={step} />
      </div>

      {error && (
        <div className="container max-w-4xl mx-auto">
          <Alert variant="destructive" className="mb-4">
            <AlertTitle>{t("transactions.file_import.error.title")}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {step === 0 && (
        <div className="container max-w-4xl mx-auto py-5">
          <UploadStep file={file} onFileChange={onFileChange} />
        </div>
      )}

      {step === 1 && (
        <div className="container max-w-6xl mx-auto py-5">
          <MappingStep
            parsed={parsed ?? []}
            mapping={mapping}
            setMapping={setMapping}
            onNext={() => setStep(2)}
            onBack={handleBack}
          />
        </div>
      )}

      {step === 2 && (
        <div className="container max-w-4xl mx-auto py-5">
          {checkDuplicatesLoading ? (
            <div className="flex items-center justify-center w-full h-[60vh]">
              <Loader2 className="w-8 h-8 animate-spin mr-2 text-gray-700" />
              <TypographyLarge className="text-gray-700">
                {t("transactions.file_import.checking_duplicates")}
              </TypographyLarge>
            </div>
          ) : (
            <BankAccountStep
              parsed={parsed ?? []}
              selectedBankAccountId={selectedBankAccountId}
              setSelectedBankAccountId={setSelectedBankAccountId}
              onNext={onAfterBankAccountStep}
              onBack={handleBack}
              isCreating={isCreating}
            />
          )}
        </div>
      )}

      {step === 3 && (
        <div className="container max-w-4xl mx-auto py-5">
          {isCreating ? (
            <div className="flex items-center justify-center w-full h-[60vh]">
              <Loader2 className="w-8 h-8 animate-spin mr-2 text-gray-700" />
              <TypographyLarge className="text-gray-700">
                {t("transactions.file_import.creating_transactions")}
              </TypographyLarge>
            </div>
          ) : (
            <ReviewStep
              onFinish={handleCreateTransactions}
              onBack={handleBack}
              completeTransactions={completeTransactions}
              incompleteTransactions={incompleteTransactions}
              duplicateTransactions={duplicateTransactions}
              checkDuplicatesLoading={checkDuplicatesLoading}
            />
          )}
        </div>
      )}

      {step === 4 && (
        <div className="container max-w-4xl mx-auto py-5">
          <SuccessBanner
            title={t("transactions.file_import.success.title")}
            description={t("transactions.file_import.success.description")}
            buttonText={t("transactions.file_import.success.button")}
            onButtonClick={goToAllTransactions}
          />
        </div>
      )}
    </div>
  );

  function onFileChange(file: File | null) {
    setFile(file);
    if (!file) {
      setParsed(null);
      return;
    }
    parseFile(file);
  }

  async function parseFile(f: File) {
    try {
      const res = await parseBankTransactionsFile(f);
      const rows = res?.rows ?? res;

      if (!rows || rows.length === 0) {
        setError(t("transactions.file_import.error.empty_rows"));
        setParsed(null);
        setFile(null);
        return;
      }

      setError(null);
      setMapping(res?.suggestedMapping ?? {});
      setParsed(rows);
      setStep((step) => step + 1);
    } catch (e) {
      console.error("Failed to parse bank transactions file", e);
      setError(t("transactions.file_import.error.parse_failed"));
      setParsed(null);
      setFile(null);
    }
  }

  async function onAfterBankAccountStep(bankAccountId: string) {
    setCheckDuplicatesLoading(true);
    let succeeded = false;
    try {
      const transactions = parseTransactionsData(
        parsed ?? [],
        mapping,
        bankAccountId,
      );

      const completeTransactionsWithDuplicates =
        filterOutIncompleteTransactions(transactions);
      const incompleteTransactions = transactions.filter(
        (t) => !completeTransactionsWithDuplicates.includes(t),
      );

      const { nonDuplicates, duplicates } = await checkTransactionsDuplicates(
        completeTransactionsWithDuplicates,
      );

      setCompleteTransactions(nonDuplicates);
      setIncompleteTransactions(incompleteTransactions);
      setDuplicateTransactions(duplicates);
      succeeded = true;
    } catch (e) {
      Sentry.captureException(e);
      setError(t("transactions.file_import.error.duplicate_check_failed"));
      toast.error(t("transactions.file_import.error.duplicate_check_failed"));
    } finally {
      setCheckDuplicatesLoading(false);
      if (succeeded) {
        setStep(3);
      }
    }
  }

  function handleClose() {
    window.history.back();
  }
  function goToAllTransactions() {
    navigate({
      to: "/$company/unternehmen/transaktionen",
      params: { company: companySlug },
    });
  }

  function handleBack() {
    setStep((prev) => {
      const next = Math.max(0, prev - 1);
      if (next === 0) {
        // Clearing file returns upload UI; also clear derived state
        setFile(null);
        setParsed(null);
        setMapping({});
        setSelectedBankAccountId("");
        setCompleteTransactions([]);
        setIncompleteTransactions([]);
        setDuplicateTransactions([]);
        setError(null);
      }
      return next;
    });
  }

  async function handleCreateTransactions() {
    if (!selectedBankAccountId) {
      return;
    }
    setIsCreating(true);
    try {
      if (!parsed) {
        throw new Error("No parsed rows");
      }

      await createTransactionsFileImport({
        variables: { input: completeTransactions },
      });
      setStep(4);
    } catch (e: any) {
      Sentry.captureException(e);
      toast.error(t("transactions.file_import.error.create_failed"));
    } finally {
      setIsCreating(false);
    }
  }
}

function filterOutIncompleteTransactions(
  transactions: Partial<Transaction>[],
): Partial<Transaction>[] {
  return transactions.filter(
    (t): t is Transaction =>
      t.amount !== undefined && t.bookingDate !== undefined,
  );
}

function parseTransactionsData(
  parsed: Record<string, string>[],
  mapping: Record<string, string>,
  selectedBankAccountId: string,
): Partial<Transaction>[] {
  return parsed.map((row) => {
    const amount = parseAmount(row[mapping["amount"]]);
    const currency = parseCurrency(row[mapping["currency"]]);
    const bookingDate = parseDate(row[mapping["bookingDate"]]);
    const originalAmount = parseAmount(row[mapping["originalAmount"]]);
    const originalCurrency = parseCurrency(row[mapping["originalCurrency"]]);
    const purpose =
      row[mapping["purpose"]] && parsePurpose(row[mapping["purpose"]]);

    const payload = {
      bankAccountId: selectedBankAccountId,
      side:
        amount.value && amount.value < 0
          ? TransactionSide.DEBIT
          : TransactionSide.CREDIT,
      amount: amount.value ? Math.abs(amount.value) : undefined,
      counterpartyName: row[mapping["counterpartyName"]],
      counterpartyIban: row[mapping["counterpartyIban"]],
      purpose: purpose && purpose.value,
      currency: currency.value ?? "EUR",
      bookingDate: bookingDate.value,
      originalAmount:
        originalAmount.value !== undefined
          ? Math.abs(originalAmount.value)
          : undefined,
      originalCurrency: originalCurrency.value,
      transactionDetails: row,
    };
    return payload;
  });
}
